<template>
    <div class="stepper-wrapper" ref="root">
        <transition-group name="component-fade">
            <div key="app_stepper_transition">
                <slot v-bind="{step, lastStep, firstStep}"/>
            </div>
        </transition-group>

        <div v-if="steps.length > 1" class="stepper-steps no-select">
            <span v-for="(stepInfo, i) in stepInfos" v-interact="() => setStep(i)" :class="['icon-interact', {'is-valid': validSteps && stepInfo.isValid}]">
                <i class="material-icons md-16" aria-hidden="true" role="img">{{stepInfo.stepId === step ? 'circle' : 'radio_button_unchecked'}}</i>
            </span>
        </div>

        <div class="stepper-buttons">
            <div v-if="cancelRedirectRoute !== null && firstStep" class="button button-inline-block" v-interact="cancel">{{cancelButtonText || 'Abbrechen'}}</div>

            <div v-if="showBackButton && !firstStep" class="button button-inline-block button-back" v-interact="() => setStep(currentIndex - 1)">Zurück</div>

            <div v-if="showLastButton || !lastStep" :class="['button button-inline-block button-next', {disabled: !currentStepValid}]"
                v-interact="() => setStep(currentIndex + 1, true)">
                {{lastStep ? lastButtonText : 'Weiter'}}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {useRouterUtils} from '@/composables/routerUtils';
    import {usePortuna} from '@/composables/portuna';
    import {type RouteLocationRaw} from 'vue-router';
    import {scrollToElement} from '../library/dom';
    import {computed, ref} from 'vue';

    const portuna = usePortuna();
    defineExpose(portuna);
    const routerUtils = useRouterUtils();
    type StepId = string | number;
    const emit             = defineEmits<{
        proceed: [];
        change: [];
    }>();
    const props            = withDefaults(
        defineProps<{
            steps: StepId[];
            validSteps?: Nullable<StepId[]>;
            showBackButton?: boolean;
            showLastButton?: boolean;
            lastButtonText: string;
            cancelRedirectRoute?: Nullable<RouteLocationRaw>;
            cancelButtonText?: Nullable<string>;
        }>(),
        {
            validSteps: null,
            showBackButton: true,
            showLastButton: true,
            cancelRedirectRoute: null,
            cancelButtonText: null,
        },
    );
    const step             = ref<StepId>(null as unknown as StepId);
    const firstStep        = computed((): boolean =>
    {
        return step.value === props.steps[0];
    });
    const lastStep         = computed((): boolean =>
    {
        return step.value === props.steps[props.steps.length - 1];
    });
    const currentIndex     = computed((): number =>
    {
        return props.steps.indexOf(step.value) || 0;
    });
    step.value             = props.steps[0];
    const isValidStep      = (step: StepId): boolean =>
    {
        return !props.validSteps || props.validSteps.includes(step);
    };
    const cancel           = (): void =>
    {
        routerUtils.routerGoBack(props.cancelRedirectRoute ?? undefined);
    };
    const stepInfos        = computed(() =>
    {
        return props.steps.map((step) =>
        {
            return {
                stepId: step,
                isValid: isValidStep(step),
            };
        });
    });
    const currentStepValid = computed(() =>
    {
        return isValidStep(step.value);
    });
    const setStep          = (newIndex: number, buttonPressed = false) =>
    {
        if (lastStep.value && buttonPressed && currentStepValid.value)
        {
            emit('proceed');
            portuna.emitter.emit('proceed');
        }
        if (
            (!currentStepValid.value && newIndex > currentIndex.value) ||
            newIndex < 0 ||
            newIndex >= props.steps.length
        )
        {
            return;
        }
        scrollToElement(portuna.root as HTMLElement);
        step.value = props.steps[newIndex];
        emit('change');
    };

</script>